import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Package,
  DollarSign,
  Tag,
  Calendar,
  TrendingUp,
  Star,
  Eye,
  Edit,
  Truck,
  AlertTriangle,
  CheckCircle,
  Clock,
  Image as ImageIcon,
  ExternalLink,
  Copy,
  Share2,
  Sparkles,
  ShoppingCart,
  Warehouse,
  Loader2,
  Globe,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { StarRating } from "./ui/star-rating";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  calculateProductRating,
  getPendingReviewsCount,
} from "../lib/utils";
import { Product } from "../types";

interface ProductDetailsModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (product: Product) => void;
  onClone?: (product: Product) => void;
}

export function ProductDetailsModal({
  product,
  isOpen,
  onClose,
  onEdit,
  onClone,
}: ProductDetailsModalProps) {
  const [shippingOptions, setShippingOptions] = React.useState<
    Array<{
      id: string;
      name: string;
      description: string;
      cost: number;
      deliveryTimeMin: number;
      deliveryTimeMax: number;
    }>
  >([]);
  const [loadingShippingOptions, setLoadingShippingOptions] =
    React.useState(false);

  // Load shipping options when modal opens
  React.useEffect(() => {
    const loadShippingOptions = async () => {
      if (!isOpen || !product?.availableShippingOptions?.length) return;

      try {
        setLoadingShippingOptions(true);
        const { doc, getDoc } = await import("firebase/firestore");
        const { db } = await import("../services/firebase");

        const configDoc = await getDoc(doc(db, "shippingConfig", "main"));
        if (configDoc.exists()) {
          const data = configDoc.data();
          const availableOptions = data.tiers
            .filter((tier: any) =>
              product.availableShippingOptions.includes(tier.id)
            )
            .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
            .map((tier: any) => ({
              id: tier.id,
              name: tier.name,
              description: tier.description,
              cost: tier.cost,
              deliveryTimeMin: tier.deliveryTimeMin,
              deliveryTimeMax: tier.deliveryTimeMax,
            }));
          setShippingOptions(availableOptions);
        }
      } catch (error) {
        console.error("Error loading shipping options:", error);
      } finally {
        setLoadingShippingOptions(false);
      }
    };

    loadShippingOptions();
  }, [isOpen, product?.availableShippingOptions]);

  const stockStatus = React.useMemo(() => {
    if (!product?.stockQuantity || product.stockQuantity === 0) {
      return {
        status: "Out of Stock",
        color: "bg-red-100 text-red-800 border-red-200",
        icon: AlertTriangle,
        iconColor: "text-red-600",
      };
    }
    if (product.stockQuantity <= (product.lowStockThreshold || 10)) {
      return {
        status: "Low Stock",
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        icon: Clock,
        iconColor: "text-yellow-600",
      };
    }
    return {
      status: "In Stock",
      color: "bg-green-100 text-green-800 border-green-200",
      icon: CheckCircle,
      iconColor: "text-green-600",
    };
  }, [product?.stockQuantity, product?.lowStockThreshold]);

  // Early return after all hooks
  if (!product) return null;

  const copyProductId = () => {
    navigator.clipboard.writeText(product.id);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
          style={{
            margin: 0,
            padding: "1rem",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{
              type: "spring",
              duration: 0.5,
              bounce: 0.3,
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Package className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Product Details
                    </motion.h2>
                    <motion.p
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm"
                    >
                      {product.name}
                    </motion.p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-lg border border-white/20">
                    <span className="text-white/90 text-sm">
                      ID: {product.id}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyProductId}
                      className="p-1 h-auto text-white/70 hover:bg-white/20 hover:text-white transition-all duration-200"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  {onClone && (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onClone(product)}
                        className="flex items-center gap-2 bg-white/20 border-white/30 text-white hover:bg-white/30 hover:text-white"
                      >
                        <Copy className="w-3 h-3" />
                        Clone
                      </Button>
                    </motion.div>
                  )}
                  {onEdit && (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(product)}
                        className="flex items-center gap-2 bg-white/20 border-white/30 text-white hover:bg-white/30 hover:text-white"
                      >
                        <Edit className="w-3 h-3" />
                        Edit
                      </Button>
                    </motion.div>
                  )}
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onClose}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              <div className="p-6 space-y-6">
                {/* Product Overview */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="grid grid-cols-1 lg:grid-cols-3 gap-6"
                >
                  {/* Price & Sales */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-10 w-10 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900">Pricing</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="text-2xl font-bold text-gray-900">
                        {formatCurrency(product.price)}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        {product.onSalePrice && (
                          <p>
                            Sale Price:{" "}
                            <span className="font-semibold text-green-600">
                              {formatCurrency(product.onSalePrice)}
                            </span>
                          </p>
                        )}
                        <p>
                          Sales:{" "}
                          <span className="font-semibold">
                            {product.salesCount || 0}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Stock Status */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                        <Warehouse className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900">Inventory</h3>
                    </div>
                    <div className="space-y-3">
                      <Badge
                        className={`${stockStatus.color} border-transparent hover:${stockStatus.color} rounded-lg px-3 py-1.5`}
                      >
                        <stockStatus.icon className="w-3 h-3 mr-1" />
                        {stockStatus.status}
                      </Badge>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          Stock:{" "}
                          <span className="font-semibold">
                            {product.stockQuantity || 0}
                          </span>
                        </p>
                        <p>
                          Threshold:{" "}
                          <span className="font-semibold">
                            {product.lowStockThreshold || 10}
                          </span>
                        </p>
                        {product.netWeight && (
                          <p>
                            Weight:{" "}
                            <span className="font-semibold">
                              {product.netWeight} {product.weightUnit}
                            </span>
                          </p>
                        )}
                        {product.listingQuantity &&
                          product.listingQuantity > 1 && (
                            <p>
                              Qty per listing:{" "}
                              <span className="font-semibold">
                                {product.listingQuantity}
                              </span>
                            </p>
                          )}
                      </div>
                    </div>
                  </div>

                  {/* Category & Brand */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-10 w-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                        <Tag className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900">
                        Category & Brand
                      </h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <span className="text-sm font-medium text-gray-700 mb-2 block">
                          Category
                        </span>
                        <Badge
                          variant="outline"
                          className="rounded-lg px-3 py-1.5"
                        >
                          {product.category || "Uncategorized"}
                        </Badge>
                      </div>

                      {product.brand && (
                        <div>
                          <span className="text-sm font-medium text-gray-700 mb-2 block">
                            Brand
                          </span>
                          <Badge
                            variant="outline"
                            className="rounded-lg px-3 py-1.5 bg-blue-50 text-blue-700 border-blue-200"
                          >
                            {product.brand}
                          </Badge>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-1 pt-2">
                        {product.isNew && (
                          <Badge className="bg-emerald-100 text-emerald-800 border-transparent text-xs px-2 py-0.5">
                            NEW
                          </Badge>
                        )}
                        {product.isBestSeller && (
                          <Badge className="bg-amber-100 text-amber-800 border-transparent text-xs px-2 py-0.5">
                            BEST
                          </Badge>
                        )}
                        {product.onSale && (
                          <Badge className="bg-red-100 text-red-800 border-transparent text-xs px-2 py-0.5">
                            SALE
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>

                <Separator />

                {/* Product Details */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                >
                  {/* Product Info */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                        <Package className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900">
                        Product Information
                      </h3>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <div>
                        <span className="text-sm font-medium text-gray-700">
                          Name:
                        </span>
                        <p className="text-gray-900 font-medium">
                          {product.name}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-700">
                          Description:
                        </span>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {product.description || "No description provided"}
                        </p>
                      </div>
                      {product.tags && product.tags.length > 0 && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">
                            Tags:
                          </span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {product.tags.map((tag, index) => (
                              <Badge
                                key={index}
                                variant="outline"
                                className="text-xs"
                              >
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Product Images */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
                        <ImageIcon className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900">
                        Product Images
                      </h3>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="aspect-square bg-gray-200 rounded-lg overflow-hidden mb-3">
                        {product.image &&
                        product.image !== "/api/placeholder/300/300" ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-12 h-12 text-gray-400" />
                          </div>
                        )}
                      </div>
                      {product.images && product.images.length > 1 && (
                        <div className="grid grid-cols-4 gap-2">
                          {product.images.slice(1, 5).map((img, index) => (
                            <div
                              key={index}
                              className="aspect-square bg-gray-200 rounded-md overflow-hidden"
                            >
                              <img
                                src={img}
                                alt={`${product.name} ${index + 2}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>

                <Separator />

                {/* Additional Information */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                >
                  {/* Shipping Options */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-gradient-to-br from-orange-600 to-amber-600 rounded-lg flex items-center justify-center">
                        <Truck className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900">
                        Shipping Options
                      </h3>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                      {loadingShippingOptions ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin text-gray-600 mr-2" />
                          <span className="text-sm text-gray-600">
                            Loading...
                          </span>
                        </div>
                      ) : shippingOptions.length > 0 ? (
                        <div className="space-y-2">
                          {shippingOptions.map((option) => (
                            <div
                              key={option.id}
                              className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                            >
                              <div>
                                <p className="font-medium text-gray-900 text-sm">
                                  {option.name}
                                </p>
                                <p className="text-xs text-gray-600">
                                  {option.description}
                                </p>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                Available
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-gray-600">
                            No shipping options configured
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-gradient-to-br from-gray-600 to-gray-700 rounded-lg flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900">Metadata</h3>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm font-medium text-gray-700">
                            Created:
                          </span>
                          <p className="text-sm text-gray-600">
                            {product.createdAt
                              ? formatDate(new Date(product.createdAt))
                              : "N/A"}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700">
                            Updated:
                          </span>
                          <p className="text-sm text-gray-600">
                            {product.updatedAt
                              ? formatDate(new Date(product.updatedAt))
                              : "N/A"}
                          </p>
                        </div>
                      </div>
                      {(product.seoTitle || product.seoDescription) && (
                        <div className="border-t border-gray-200 pt-3">
                          <span className="text-sm font-medium text-gray-700">
                            SEO:
                          </span>
                          {product.seoTitle && (
                            <p className="text-xs text-gray-600 mt-1">
                              Title: {product.seoTitle}
                            </p>
                          )}
                          {product.seoDescription && (
                            <p className="text-xs text-gray-600 mt-1">
                              Description: {product.seoDescription}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>

                {/* Customs Information */}
                {(product.manufacturer || product.hsCode || product.originCountry) && (
                  <>
                    <Separator />
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.55 }}
                      className="space-y-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-gradient-to-br from-red-600 to-rose-600 rounded-lg flex items-center justify-center">
                          <Globe className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="font-semibold text-gray-900">Customs Information</h3>
                      </div>
                      
                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {product.manufacturer && (
                            <div>
                              <span className="text-sm font-medium text-gray-700 block mb-1">
                                Manufacturer
                              </span>
                              <p className="text-sm text-gray-900 font-medium bg-white p-2 rounded-lg border border-gray-200">
                                {product.manufacturer}
                              </p>
                            </div>
                          )}
                          
                          {product.hsCode && (
                            <div>
                              <span className="text-sm font-medium text-gray-700 block mb-1">
                                HS Tariff Code
                              </span>
                              <p className="text-sm text-gray-900 font-mono font-medium bg-white p-2 rounded-lg border border-gray-200">
                                {product.hsCode}
                              </p>
                            </div>
                          )}
                          
                          {product.originCountry && (
                            <div>
                              <span className="text-sm font-medium text-gray-700 block mb-1">
                                Country of Origin
                              </span>
                              <p className="text-sm text-gray-900 font-medium bg-white p-2 rounded-lg border border-gray-200">
                                {product.originCountry}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs text-gray-500 leading-relaxed">
                            This information is used for international shipping customs declarations and compliance requirements.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </>
                )}

                {/* Reviews Section */}
                {product.reviews && product.reviews.length > 0 && (
                  <>
                    <Separator />
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="space-y-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-gradient-to-br from-yellow-600 to-orange-600 rounded-lg flex items-center justify-center">
                          <Star className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="font-semibold text-gray-900">
                          Recent Reviews ({product.reviews.length})
                        </h3>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {product.reviews.slice(0, 5).map((review, index) => (
                            <div
                              key={index}
                              className="bg-white rounded-lg p-3 border border-gray-200"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-7 h-7 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs font-medium">
                                    {review.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900 text-sm">
                                      {review.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {formatDate(new Date(review.date))}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <StarRating
                                    rating={review.rating}
                                    size="sm"
                                  />
                                  <Badge
                                    variant="outline"
                                    className={`text-xs ${
                                      review.status === "verified"
                                        ? "bg-green-50 text-green-700 border-green-200"
                                        : "bg-yellow-50 text-yellow-700 border-yellow-200"
                                    }`}
                                  >
                                    {review.status}
                                  </Badge>
                                </div>
                              </div>
                              <p className="text-sm text-gray-600 leading-relaxed">
                                {review.content}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
